import { useEffect, useRef } from "react";
import { loadGoogleMapsScript } from "./utils/loadGoogleMapsScript";

interface LatLng {
  lat(): number;
  lng(): number;
}

export interface LatLngLiteral {
  lat: number;
  lng: number;
}

interface GeocoderResult {
  geometry: {
    location: LatLng;
  };
}

type GeocoderStatus =
  | "OK"
  | "ZERO_RESULTS"
  | "OVER_DAILY_LIMIT"
  | "OVER_QUERY_LIMIT"
  | "REQUEST_DENIED"
  | "INVALID_REQUEST"
  | "UNKNOWN_ERROR";

interface GeocoderRequest {
  address?: string;
  location?: LatLngLiteral;
}

interface Geocoder {
  geocode(
    request: GeocoderRequest,
    callback: (results: GeocoderResult[], status: GeocoderStatus) => void,
  ): void;
}

interface MapOptions {
  center: LatLngLiteral;
  zoom: number;
  disableDefaultUI?: boolean;
  zoomControl?: boolean;
  mapTypeControl?: boolean;
  streetViewControl?: boolean;
  rotateControl?: boolean;
  fullscreenControl?: boolean;
  draggable?: boolean;
  scrollwheel?: boolean;
  disableDoubleClickZoom?: boolean;
  gestureHandling?: string;
}

// Map instance — we only need the constructor return value as a reference
interface GoogleMapInstance {}

interface MarkerOptions {
  position: LatLngLiteral;
  map: GoogleMapInstance;
  title?: string;
}

interface GoogleMapsNamespace {
  Map: new (el: HTMLElement, options: MapOptions) => GoogleMapInstance;
  Marker: new (options: MarkerOptions) => void;
  Geocoder: new () => Geocoder;
}

declare global {
  interface Window {
    google: {
      maps: GoogleMapsNamespace;
    };
  }
}

// ── Static map options (shared across all instances) ──────────────────────────
// Defined outside the component to avoid object recreation on every render.

const BASE_MAP_OPTIONS: Omit<MapOptions, "center"> = {
  zoom: 15,
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  draggable: false,
  scrollwheel: false,
  disableDoubleClickZoom: true,
  gestureHandling: "none",
};

// ── Props ─────────────────────────────────────────────────────────────────────

export interface GoogleMapEmbedProps {
  readonly lat: number;
  readonly lng: number;
  /** Marker label and map title. */
  readonly name: string;
  /** Full address string — only used when lat/lng are invalid. */
  readonly address: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function GoogleMapEmbed({
  lat,
  lng,
  name,
  address,
}: GoogleMapEmbedProps): JSX.Element {
  const mapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const renderMap = (location: LatLngLiteral): void => {
      if (!mapRef.current || !window.google?.maps) return;
      const map = new window.google.maps.Map(mapRef.current, {
        ...BASE_MAP_OPTIONS,
        center: location,
      });
      new window.google.maps.Marker({
        position: location,
        map,
        title: name,
      });
    };

    const initMap = (): void => {
      // Prefer lat/lng — avoids geocoding quota entirely
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        renderMap({ lat, lng });
        return;
      }

      // Fallback: geocode the full address string
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { address },
        (results: GeocoderResult[], status: GeocoderStatus) => {
          if (status === "OK" && results[0]) {
            renderMap({
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            });
          } else {
            console.warn(`Geocode failed for "${address}":`, status);
          }
        },
      );
    };

    loadGoogleMapsScript().then(initMap).catch(console.error);
  }, [lat, lng, address]);

  return (
    <div
      ref={mapRef}
      data-testid="mapDiv"
      style={{ width: "100%", height: "100%" }}
    />
  );
}
